import { TaJson } from "ta-json";

import { Nullable, NullableResultPromise } from "../../base-types";
import { IExtendedContentHubClient } from "../../clients/extended-client";
import { DEFINITIONS } from "../../constants";
import { Entity, IEntity } from "../base/entity";
import { EntityConstructionArgs } from "../base/entity-construction-args";
import { IChildToManyParentsRelation } from "../base/relations/child-to-many-parents-relation";
import { IParentToOneChildRelation } from "../base/relations/parent-to-one-child-relation";
import { IUserPolicy } from "../policies/user-policy";
import { IEntityLoadConfiguration } from "../querying/entity-load-configuration";
import { IUserGroupConfiguration, UserGroupConfiguration } from "./user-group-configuration";
import { IUserProfile } from "./user-profile";

export interface IUser extends IEntity {
    username: Nullable<string>;

    userGroupConfiguration: Nullable<IUserGroupConfiguration>;

    getUserProfileAsync(loadConfiguration?: Nullable<IEntityLoadConfiguration>): NullableResultPromise<IUserProfile>;

    getUserGroupIdsAsync(): NullableResultPromise<Array<number>>;

    getUserPolicyAsync(): NullableResultPromise<IUserPolicy>;
}

export class User extends Entity implements IUser {
    get username(): Nullable<string> {
        return this.getPropertyValue<"String">(DEFINITIONS["User"].username);
    }

    set username(value: Nullable<string>) {
        this.setPropertyValue(DEFINITIONS["User"].username, value);
    }

    get userGroupConfiguration(): Nullable<IUserGroupConfiguration> {
        const token = this.getPropertyValue<"Json">(DEFINITIONS["User"].userGroupConfiguration);

        if (token == null) {
            return null;
        }

        return TaJson.deserialize<UserGroupConfiguration>(token, UserGroupConfiguration);
    }

    constructor(client: IExtendedContentHubClient, args: EntityConstructionArgs) {
        super(client, args);
    }

    async getUserProfileAsync(
        loadConfiguration?: Nullable<IEntityLoadConfiguration>
    ): NullableResultPromise<IUserProfile> {
        const profileRelation = (await this.getRelationAsync(
            DEFINITIONS["User"].userToUserProfile
        )) as IParentToOneChildRelation;

        if (profileRelation == null || profileRelation.child == null) {
            return null;
        } else {
            const profile = await this._client.entities.getAsync(profileRelation.child, loadConfiguration);

            return profile as IUserProfile;
        }
    }

    async getUserGroupIdsAsync(): NullableResultPromise<Array<number>> {
        const profileRelation = (await this.getRelationAsync(
            DEFINITIONS["User"].userGroupToUser
        )) as IChildToManyParentsRelation;

        if (profileRelation == null) {
            return null;
        }

        return profileRelation.parents;
    }

    async getUserPolicyAsync(): NullableResultPromise<IUserPolicy> {
        if (this.id == null) {
            return null;
        }

        return this._client.policies.getUserPolicyAsync(this.id);
    }
}
